import { useMemo } from 'react';
import { localDateKey } from '../utils/overdue';
import { settledSoldLines } from '../utils/invoicingView';
import { accountBalanceTile, lastInvoicedTile, settledInvoices } from '../utils/invoicingSummary';
import type { SummaryTile } from '../utils/invoicingSummary';
import { stillToInvoiceContent } from '../utils/stillToInvoice';
import { stillToInvoicePanel } from '../utils/stillToInvoicePanel';
import type { StillToInvoicePanelModel } from '../utils/stillToInvoicePanel';
import type { AgreementInvoicesQuery, SoldItemsQuery } from '../types/agreements.types';

export interface StillToInvoiceView {
    isLoading: boolean;
    isError: boolean;
    refetch: () => unknown;
    content:
        | { everythingInvoiced: true }
        | { everythingInvoiced: false; panel: StillToInvoicePanelModel }
        | undefined;
}

export interface InvoicingOverview {
    tiles: SummaryTile[];
    stillToInvoice: StillToInvoiceView;
}

interface Input {
    invoicesQuery: AgreementInvoicesQuery;
    soldItemsQuery: SoldItemsQuery;
    availableBalance: string | null;
    balanceUnreadableReason: string | null;
}

export function useInvoicingOverview({
    invoicesQuery,
    soldItemsQuery,
    availableBalance,
    balanceUnreadableReason,
}: Input): InvoicingOverview {
    const lines = settledSoldLines(soldItemsQuery);
    const invoices = settledInvoices(invoicesQuery);
    const today = localDateKey(new Date());

    const panelContent = useMemo(() => {
        if (!lines) return undefined;
        const rawContent = stillToInvoiceContent(lines);
        return rawContent.everythingInvoiced
            ? ({ everythingInvoiced: true } as const)
            : ({ everythingInvoiced: false, panel: stillToInvoicePanel(rawContent, today) } as const);
    }, [lines, today]);

    const tiles = useMemo(
        () => [accountBalanceTile(availableBalance, balanceUnreadableReason), lastInvoicedTile(invoices)],
        [invoices, availableBalance, balanceUnreadableReason],
    );

    return {
        tiles,
        stillToInvoice: {
            isLoading: soldItemsQuery.isLoading,
            isError: soldItemsQuery.isError,
            refetch: soldItemsQuery.refetch,
            content: panelContent,
        },
    };
}
